import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import masterService from "../../services/masterService";
import authService from "../../services/authService";
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  IconButton,
  Avatar,
  TextField,
  Button,
  Alert,
  CircularProgress,
  useTheme,
  Chip,
  Fade,
  Autocomplete,
  Card,
  CardContent,
  Divider,
  alpha,
} from "@mui/material";
import {
  CameraAlt as CameraIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  Stars as StarsIcon,
  Info as InfoIcon,
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  AccessTime as AccessTimeIcon,
  ContactMail as ContactIcon,
  FamilyRestroom as FamilyIcon,
  Work as WorkIcon,
  AttachMoney as MoneyIcon,
  Favorite as FavoriteIcon,
} from "@mui/icons-material";
import { LocalizationProvider, DatePicker, TimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, parse } from "date-fns";

// Helper function to clean and validate dropdown data
const cleanDropdownData = (data) => {
  if (!Array.isArray(data)) return [];
  
  // Remove duplicates based on id and ensure proper structure
  const seen = new Set();
  const cleaned = data.filter(item => {
    if (!item || typeof item !== 'object') return false;
    
    const id = item.id || item.value;
    if (!id || seen.has(id)) return false;
    
    seen.add(id);
    
    // Ensure item has both id and name properties
    if (!item.name && item.value) {
      item.name = item.value;
    }
    
    return true;
  });
  
  
  return cleaned;
};

const Profile = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    mobile: "",
    age: "",
    gender: "",
    country: "",
    state: "",
    city: "",
    password: "",
    qualification: "",
    date_of_birth: null,
    birth_time: null,
    birth_place: "",
    raasi: "",
    nakshatra: "",
    padam: "",
    caste: "",
    caste_category: "",
    gothram: "",
    height_cm: "",
    complexion: "",
    marital_status: "",
    religion: "",
    occupation: "",
    father_name: "",
    father_occupation: "",
    mother_name: "",
    mother_occupation: "",
    family_location: "",
    // siblings_info: "",
    brother_count: "",
    sister_count: "",
    preferred_call_time: "",
    // future_duration: "",
    rc_in_groom_style: "",
    em_in_groom_style: "",
    mw_in_bride_style: "",
    hm_package: "",
    sil_wishList: "",
    silver_ware: "",
    gold_jewelry: "",
    income: "",
    flat: [],
    land: [],
  });

  const [profileImage, setProfileImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState("");
  const [userLoading, setUserLoading] = useState(true);

  const [dropdowns, setDropdowns] = useState({
    countries: [],
    states: [],
    cities: [],
    qualifications: [],
    raasis: [],
    nakshatras: [],
    padams: [],
    castes: [],
    casteCategories: [],
  });

  const [imageFile, setImageFile] = useState(null);

  const genderOptions = useMemo(
    () => [
      { id: "male", name: "Male" },
      { id: "female", name: "Female" },
      { id: "other", name: "Other" },
    ],
    []
  );

  const yesNoOptions = useMemo(
    () => [
      { id: "Yes", name: "Yes" },
      { id: "No", name: "No" },
    ],
    []
  );

  const maritalStatusOptions = useMemo(
    () => [
      { id: "Single", name: "Single" },
      { id: "Married", name: "Married" },
      { id: "Divorced", name: "Divorced" },
      { id: "Widowed", name: "Widowed" },
    ],
    []
  );

  const incomeOptions = useMemo(
    () => [
      { id: "< 10L", name: "< 10L" },
      { id: "10-30L", name: "10-30L" },
      { id: "30-50L", name: "30-50L" },
      { id: "> 50L", name: "> 50L" },
    ],
    []
  );

  const flatOptions = useMemo(
    () => [
      { id: "highrise", name: "Highrise" },
      { id: "lowrise", name: "Lowrise" },
    ],
    []
  );

  const landOptions = useMemo(
    () => [
      { id: "Agricultural", name: "Agricultural" },
      { id: "Residential", name: "Residential" },
      { id: "Commercial", name: "Commercial" },
    ],
    []
  );

  const heightOptions = useMemo(
    () => [
      { id: "5ft 2in - 157cm", name: "5ft 2in - 157cm" },
      { id: "5ft 3in - 160cm", name: "5ft 3in - 160cm" },
      { id: "5ft 4in - 162cm", name: "5ft 4in - 162cm" },
      { id: "5ft 5in - 165cm", name: "5ft 5in - 165cm" },
      { id: "5ft 6in - 167cm", name: "5ft 6in - 167cm" },
      { id: "5ft 7in - 170cm", name: "5ft 7in - 170cm" },
      { id: "5ft 8in - 172cm", name: "5ft 8in - 172cm" },
      { id: "5ft 9in - 175cm", name: "5ft 9in - 175cm" },
      { id: "5ft 10in - 177cm", name: "5ft 10in - 177cm" },
      { id: "5ft 11in - 180cm", name: "5ft 11in - 180cm" },
      { id: "6ft - 182cm", name: "6ft - 182cm" },
      { id: "6ft 1in - 185cm", name: "6ft 1in - 185cm" },
      { id: "6ft 2in - 187cm", name: "6ft 2in - 187cm" },
    ],
    []
  );

  const religionOptions = useMemo(
    () => [
      { id: "hindu", name: "Hindu" },
      { id: "muslim", name: "Muslim" },
      { id: "christian", name: "Christian" },
      { id: "sikh", name: "Sikh" },
      { id: "buddhist", name: "Buddhist" },
      { id: "jain", name: "Jain" },
      { id: "other", name: "Other" },
    ],
    []
  );

  const preferredCallTimeOptions = useMemo(
    () => [
      { id: "8 AM - 12 PM", name: "8 AM - 12 PM" },
      { id: "12 PM - 4 PM", name: "12 PM - 4 PM" },
      { id: "4 PM - 8 PM", name: "4 PM - 8 PM" },
      { id: "8PM - 10 PM", name: "8 PM - 10 PM" },
      { id: "Any Time", name: "Any Time" },
    ],
    []
  );

  const complexionOptions = useMemo(
    () => [
      { id: "Very Fair", name: "Very Fair" },
      { id: "Fair", name: "Fair" },
      { id: "Wheatish", name: "Wheatish" },
      { id: "Wheatish Brown", name: "Wheatish Brown" },
      { id: "Dusky", name: "Dusky" },
      { id: "Dark", name: "Dark" },
    ],
    []
  );

  const occupationOptions = useMemo(
    () => [
      { id: "Business", name: "Business" },
      { id: "Govt Employee", name: "Govt Employee" },
      { id: "Private Employee", name: "Private Employee" },
      { id: "Other", name: "Other" },
      { id: "Home Maker", name: "Home Maker" },
      { id: "Expired", name: "Expired" },
    ],
    []
  );

  const fieldSx = useMemo(
    () => ({
      borderRadius: "16px",
      backgroundColor: "rgba(255,255,255,0.98)",
      minWidth: 280,
      maxWidth: 380,
      fontSize: "0.95rem",
      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
      transition: "all 0.3s ease",
      "& .MuiOutlinedInput-root": {
        borderRadius: "16px",
        "&:hover": { 
          boxShadow: "0 6px 25px rgba(0,0,0,0.12)",
          transform: "translateY(-2px)",
        },
        "&:hover fieldset": { 
          borderColor: theme.palette.primary.light,
          borderWidth: "2px",
        },
        "&.Mui-focused fieldset": {
          borderColor: theme.palette.primary.main,
          borderWidth: "2px",
          boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`,
        },
      },
      "& .MuiInputLabel-root": {
        color: theme.palette.text.secondary,
        fontWeight: 500,
        "&.Mui-focused": { 
          color: theme.palette.primary.main,
          fontWeight: 600,
        },
      },
      "& .MuiAutocomplete-root": {
        borderRadius: "16px",
      },
    }),
    [theme]
  );

  const formSections = useMemo(
    () => [
      {
        title: "Basic Information",
        icon: <ContactIcon />,
        fields: [
          { name: "first_name", label: "First Name", type: "text", required: true },
          { name: "last_name", label: "Last Name", type: "text" },
          { name: "email", label: "Email", type: "email", required: true },
          { name: "mobile", label: "Mobile", type: "tel" },
          { name: "age", label: "Age", type: "number" },
          { name: "gender", label: "Gender", type: "select", options: genderOptions },
        ],
      },
      {
        title: "Personal Details",
        icon: <InfoIcon />,
        fields: [
          { name: "date_of_birth", label: "Date of Birth", type: "datePicker" },
          { name: "birth_time", label: "Birth Time", type: "timePicker" },
          { name: "birth_place", label: "Birth Place", type: "text" },
          { name: "height_cm", label: "Height", type: "select", options: heightOptions },
          { name: "complexion", label: "Complexion", type: "select", options: complexionOptions },
          { name: "marital_status", label: "Marital Status", type: "select", options: maritalStatusOptions },
          { name: "religion", label: "Religion", type: "select", options: religionOptions },
          { name: "occupation", label: "Occupation", type: "text" },
          {
            name: "qualification",
            label: "Qualification",
            type: "select",
            options: dropdowns.qualifications,
          },
        ],
      },
      {
        title: "Family Details",
        icon: <FamilyIcon />,
        fields: [
          { name: "father_name", label: "Father's Name", type: "text" },
          { name: "father_occupation", label: "Father's Occupation", type: "select", options: occupationOptions },
          { name: "mother_name", label: "Mother's Name", type: "text" },
          { name: "mother_occupation", label: "Mother's Occupation", type: "select", options: occupationOptions },
          { name: "family_location", label: "Family Location", type: "text" },
          // {
          //   name: "siblings_info",
          //   label: "Siblings Information",
          //   type: "text",
          //   multiline: true,
          //   rows: 2,
          // },
          { name: "brother_count", label: "Brothers Count", type: "number" },
          { name: "sister_count", label: "Sisters Count", type: "number" },
        ],
      },
      {
        title: "Astrological Details",
        icon: <StarsIcon />,
        fields: [
          { name: "raasi", label: "Raasi", type: "select", options: dropdowns.raasis },
          { name: "nakshatra", label: "Nakshatra", type: "select", options: dropdowns.nakshatras },
          { name: "padam", label: "Padam", type: "select", options: dropdowns.padams },
          { name: "gothram", label: "Gothram", type: "text" },
          { name: "caste", label: "Caste", type: "select", options: dropdowns.castes },
          {
            name: "caste_category",
            label: "Caste Category",
            type: "select",
            options: dropdowns.casteCategories,
          },
        ],
      },
      {
        title: "Location Information",
        icon: <LocationIcon />,
        fields: [
          { name: "country", label: "Country", type: "select", options: dropdowns.countries },
          { name: "state", label: "State", type: "select", options: dropdowns.states },
          { name: "city", label: "City", type: "select", options: dropdowns.cities },
          {
            name: "preferred_call_time",
            label: "Preferred Call Time",
            type: "select",
            options: preferredCallTimeOptions,
          },
          // { name: "future_duration", label: "Future Duration", type: "text" },
        ],
      },
      {
        title: "Financial & Property Details",
        icon: <MoneyIcon />,
        fields: [
          { name: "income", label: "Income", type: "select", options: incomeOptions },
          { name: "flat", label: "Flat Type", type: "multiSelect", options: flatOptions },
          { name: "land", label: "Land Type", type: "multiSelect", options: landOptions },
        ],
      },
      {
        title: "Groom/Bride Style Preferences",
        icon: <FavoriteIcon />,
        fields: [
          { name: "rc_in_groom_style", label: "RC in Groom Style", type: "select", options: yesNoOptions },
          { name: "em_in_groom_style", label: "EM in Groom Style", type: "select", options: yesNoOptions },
          { name: "mw_in_bride_style", label: "MW in Bride Style", type: "select", options: yesNoOptions },
          { name: "hm_package", label: "HM Package", type: "select", options: yesNoOptions },
          { name: "sil_wishList", label: "SIL WishList", type: "select", options: yesNoOptions },
          { name: "silver_ware", label: "Silver Ware", type: "select", options: yesNoOptions },
          { name: "gold_jewelry", label: "Gold Jewelry", type: "select", options: yesNoOptions },
        ],
      },
    ],
    [dropdowns, genderOptions, maritalStatusOptions, incomeOptions, flatOptions, landOptions, yesNoOptions, heightOptions, religionOptions, preferredCallTimeOptions, complexionOptions, occupationOptions]
  );

  useEffect(() => {
    if (typeof currentUser !== "undefined") {
      setUserLoading(false);
      if (currentUser) {
        const BASE_IMAGE_URL = process.env.REACT_APP_IMAGE_BASE_URL;
        setProfileImage(currentUser.image ? `${BASE_IMAGE_URL}/${currentUser.image}` : null);
        
        
        setFormData(prevFormData => ({
          ...prevFormData,
          ...Object.fromEntries(
            Object.entries(currentUser).map(([key, value]) => [
              key,
              key === "date_of_birth" && value
                ? parse(value, "yyyy-MM-dd", new Date())
                : key === "birth_time" && value
                ? parse(value, "HH:mm:ss", new Date())
                : (key === "flat" || key === "land") && value
                ? (Array.isArray(value) ? value : value.split(',').filter(v => v.trim()))
                : value || "",
            ])
          ),
          password: "",
        }));
      }
    }
  }, [currentUser]);

  const fetchDropdowns = useCallback(async () => {
    if (!currentUser) return;
    
    setDropdownLoading(true);
    try {
      const [
        countryResponse,
        qualificationResponse,
        raasiResponse,
        nakshatraResponse,
        casteResponse,
        casteCategoryResponse,
      ] = await Promise.all([
        masterService.getCountryList(),
        masterService.getQualificationList(),
        masterService.getRaasiList(),
        masterService.getNakshatraList(),
        masterService.getCasteList(),
        masterService.getCasteCategoryList(),
      ]);

      setDropdowns((prev) => ({
        ...prev,
        countries: cleanDropdownData(countryResponse),
        qualifications: cleanDropdownData(qualificationResponse),
        raasis: cleanDropdownData(raasiResponse),
        nakshatras: cleanDropdownData(nakshatraResponse),
        castes: cleanDropdownData(casteResponse),
        casteCategories: cleanDropdownData(casteCategoryResponse),
      }));


      // Load states if country is available
      if (currentUser.country) {
        try {
          const statesData = await masterService.getStatesList(currentUser.country);
          setDropdowns((prev) => ({ ...prev, states: cleanDropdownData(statesData) }));
          
          // Load cities if state is available
          if (currentUser.state) {
            const citiesData = await masterService.getCitiesList(currentUser.state);
            setDropdowns((prev) => ({ ...prev, cities: cleanDropdownData(citiesData) }));
          }
        } catch (error) {
          // Error loading states/cities
        }
      }

      if (currentUser.nakshatra) {
        try {
          const padamsData = await masterService.getPadamList(currentUser.nakshatra);
          setDropdowns((prev) => ({ ...prev, padams: cleanDropdownData(padamsData) }));
        } catch (error) {
          // Error loading padams
        }
      }
    } catch (error) {
      setErrors({ submit: "Failed to load dropdown data" });
    } finally {
      setDropdownLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    fetchDropdowns();
  }, [currentUser, fetchDropdowns]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (userLoading) {
        setUserLoading(false);
        setErrors({ submit: "Failed to load user data. Please try again." });
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [userLoading]);

  const validateForm = () => {
    const newErrors = {};
    
    // Basic Information validations
    if (!formData.first_name.trim()) {
      newErrors.first_name = "First name is required";
    } else if (formData.first_name.trim().length < 2) {
      newErrors.first_name = "First name must be at least 2 characters";
    } else if (formData.first_name.trim().length > 100) {
      newErrors.first_name = "First name cannot exceed 100 characters";
    } else if (!/^[a-zA-Z\s]+$/.test(formData.first_name.trim())) {
      newErrors.first_name = "First name can only contain letters and spaces";
    }
    
    if (formData.last_name.trim() && formData.last_name.trim().length < 2) {
      newErrors.last_name = "Last name must be at least 2 characters";
    } else if (formData.last_name.trim() && formData.last_name.trim().length > 100) {
      newErrors.last_name = "Last name cannot exceed 100 characters";
    } else if (formData.last_name.trim() && !/^[a-zA-Z\s]+$/.test(formData.last_name.trim())) {
      newErrors.last_name = "Last name can only contain letters and spaces";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = "Please enter a valid email address";
    }
    
    if (formData.mobile) {
      if (!/^\d{10,12}$/.test(formData.mobile.trim())) {
        newErrors.mobile = "Mobile number must be between 10 and 12 digits";
      } else if (!/^[6-9]\d{9,11}$/.test(formData.mobile.trim())) {
        newErrors.mobile = "Mobile number must start with 6, 7, 8, or 9";
      }
    }
    
    if (formData.age) {
      const ageNum = parseInt(formData.age);
      if (isNaN(ageNum) || ageNum < 18 || ageNum > 100) {
        newErrors.age = "Age must be between 18 and 100";
      }
    }
    
    // Personal Details validations
    if (formData.date_of_birth) {
      const today = new Date();
      const birthDate = new Date(formData.date_of_birth);
      let calculatedAge = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        calculatedAge--;
      }
      
      if (calculatedAge < 18) {
        newErrors.date_of_birth = "You must be at least 18 years old";
      } else if (calculatedAge > 100) {
        newErrors.date_of_birth = "Please enter a valid birth date";
      }
    }
    
    if (formData.height_cm) {
      if (!heightOptions.some(h => h.id === formData.height_cm)) {
        newErrors.height_cm = "Please select a valid height";
      }
    }
    
    if (formData.complexion) {
      if (!complexionOptions.some(c => c.id === formData.complexion)) {
        newErrors.complexion = "Please select a valid complexion";
      }
    }
    
    // Family Details validations
    if (formData.brother_count) {
      const brotherCount = parseInt(formData.brother_count);
      if (isNaN(brotherCount) || brotherCount < 0 || brotherCount > 20) {
        newErrors.brother_count = "Brother count must be between 0 and 20";
      }
    }
    
    if (formData.sister_count) {
      const sisterCount = parseInt(formData.sister_count);
      if (isNaN(sisterCount) || sisterCount < 0 || sisterCount > 20) {
        newErrors.sister_count = "Sister count must be between 0 and 20";
      }
    }
    
    // Location validations
    if (formData.state && !formData.country) {
      newErrors.country = "Please select a country first";
    }
    
    if (formData.city && !formData.state) {
      newErrors.state = "Please select a state first";
    }
    
    return newErrors;
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'first_name':
        if (!value.trim()) return "First name is required";
        if (value.trim().length < 2) return "First name must be at least 2 characters";
        if (value.trim().length > 100) return "First name cannot exceed 100 characters";
        if (!/^[a-zA-Z\s]+$/.test(value.trim())) return "First name can only contain letters and spaces";
        return "";
        
      case 'last_name':
        if (value.trim() && value.trim().length < 2) return "Last name must be at least 2 characters";
        if (value.trim() && value.trim().length > 100) return "Last name cannot exceed 100 characters";
        if (value.trim() && !/^[a-zA-Z\s]+$/.test(value.trim())) return "Last name can only contain letters and spaces";
        return "";
        
      case 'email':
        if (!value.trim()) return "Email is required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return "Please enter a valid email address";
        return "";
        
      case 'mobile':
        if (value && !/^\d{10,12}$/.test(value.trim())) return "Mobile number must be between 10 and 12 digits";
        if (value && !/^[6-9]\d{9,11}$/.test(value.trim())) return "Mobile number must start with 6, 7, 8, or 9";
        return "";
        
      case 'age':
        if (value) {
          const ageNum = parseInt(value);
          if (isNaN(ageNum) || ageNum < 18 || ageNum > 100) return "Age must be between 18 and 100";
        }
        return "";
        
      case 'height_cm':
        if (value && !heightOptions.some(h => h.id === value)) {
          return "Please select a valid height";
        }
        return "";
        
      case 'complexion':
        if (value && !complexionOptions.some(c => c.id === value)) {
          return "Please select a valid complexion";
        }
        return "";
        
      case 'brother_count':
        if (value) {
          const count = parseInt(value);
          if (isNaN(count) || count < 0 || count > 20) return "Brother count must be between 0 and 20";
        }
        return "";
        
      case 'sister_count':
        if (value) {
          const count = parseInt(value);
          if (isNaN(count) || count < 0 || count > 20) return "Sister count must be between 0 and 20";
        }
        return "";
        
      case 'father_occupation':
      case 'mother_occupation':
        if (value && !occupationOptions.some(option => 
          option.id === value || option.name === value
        )) {
          return "Please select a valid occupation";
        }
        return "";
        
      case 'flat':
      case 'land':
        if (Array.isArray(value) && value.length > 0) {
          // Validate that all selected values are valid options
          const validOptions = name === 'flat' ? flatOptions : landOptions;
          const invalidValues = value.filter(val => 
            !validOptions.some(option => 
              option.id === val || option.value === val || option.name === val
            )
          );
          if (invalidValues.length > 0) {
            return `Invalid selection: ${invalidValues.join(', ')}`;
          }
        }
        return "";
        
      default:
        return "";
    }
  };

  const handleChange = async (e, fieldName) => {
    const { name = fieldName, value } = e.target || {};
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Real-time validation
    const fieldError = validateField(name, value);
    if (fieldError) {
      setErrors(prev => ({ ...prev, [name]: fieldError }));
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    if (name === "country") {
      setFormData((prev) => ({ ...prev, state: "", city: "" }));
      setDropdowns((prev) => ({ ...prev, states: [], cities: [] }));
      try {
        const statesList = await masterService.getStatesList(value);
        setDropdowns((prev) => ({ ...prev, states: cleanDropdownData(statesList) }));
      } catch (error) {
        // Error loading states
      }
    }

    if (name === "state") {
      setFormData((prev) => ({ ...prev, city: "" }));
      setDropdowns((prev) => ({ ...prev, cities: [] }));
      try {
        const citiesList = await masterService.getCitiesList(value);
        setDropdowns((prev) => ({ ...prev, cities: cleanDropdownData(citiesList) }));
      } catch (error) {
        // Error loading cities
      }
    }

    if (name === "nakshatra") {
      try {
        const padamsList = await masterService.getPadamList(value);
        setDropdowns((prev) => ({ ...prev, padams: cleanDropdownData(padamsList) }));
      } catch (error) {
        // Error loading padams
      }
    }
  };

  const handleDateTimeChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.match("image.*")) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setProfileImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleImageUpload = async () => {
    if (!imageFile) return;
    setLoading(true);
    try {
      const response = await authService.uploadProfileImage(imageFile);
      setSuccess("Profile image updated!");
      setProfileImage(response.image_url);
      setImageFile(null);
      setTimeout(() => setSuccess(""), 3000);
      setErrors({});
    } catch (error) {
      setErrors({ submit: error.message || "Failed to upload image" });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (key === "date_of_birth" && value) {
          formDataToSend.append(key, format(value, "yyyy-MM-dd"));
        } else if (key === "birth_time" && value) {
          formDataToSend.append(key, format(value, "HH:mm:ss"));
        } else if ((key === "flat" || key === "land") && Array.isArray(value) && value.length > 0) {
          // For multi-select fields, send as comma-separated string
          formDataToSend.append(key, value.join(','));
        } else if (value !== null && value !== undefined && !Array.isArray(value)) {
          formDataToSend.append(key, value);
        }
      });

      const response = await authService.updateProfile(formDataToSend);
      setSuccess("Profile updated successfully!");
      setTimeout(() => setSuccess(""), 3000);
      setErrors({});

      if (response.user) {
        setFormData(prevFormData => ({
          ...prevFormData,
          ...Object.fromEntries(
            Object.entries(response.user).map(([key, value]) => [
              key,
              key === "date_of_birth" && value
                ? parse(value, "yyyy-MM-dd", new Date())
                : key === "birth_time" && value
                ? parse(value, "HH:mm:ss", new Date())
                : (key === "flat" || key === "land") && value
                ? (Array.isArray(value) ? value : value.split(',').filter(v => v.trim()))
                : value || "",
            ])
          ),
          password: "",
        }));

        if (response.user.country) {
          try {
            const statesData = await masterService.getStatesList(response.user.country);
            setDropdowns((prev) => ({
              ...prev,
              states: cleanDropdownData(statesData),
              cities: response.user.state
                ? prev.cities
                : [],
            }));
            if (response.user.state) {
              const citiesData = await masterService.getCitiesList(response.user.state);
              setDropdowns((prev) => ({ ...prev, cities: cleanDropdownData(citiesData) }));
            }
          } catch (error) {
            setErrors({ submit: "Failed to reload location data" });
          }
        } else {
          setDropdowns((prev) => ({ ...prev, states: [], cities: [] }));
        }
      }
    } catch (error) {
      setErrors({ submit: error.message || "Failed to update profile" });
    } finally {
      setLoading(false);
    }
  };

  const renderField = ({ name, label, type = "text", required = false, options = [], multiline = false, rows = 1 }) => {
    // Create a unique key by combining section info with field name
    const uniqueKey = `${name}_${label}`;
    
    if (type === "datePicker") {
      return (
        <Grid item xs={12} md={4} key={uniqueKey}>
          <DatePicker
            label={label}
            value={formData[name]}
            onChange={(value) => handleDateTimeChange(name, value)}
            slotProps={{
              textField: {
                fullWidth: true,
                error: !!errors[name],
                helperText: errors[name],
                sx: fieldSx,
                size: "medium",
              },
            }}
          />
        </Grid>
      );
    }

    if (type === "timePicker") {
      return (
        <Grid item xs={12} md={4} key={uniqueKey}>
          <TimePicker
            label={label}
            value={formData[name]}
            onChange={(value) => handleDateTimeChange(name, value)}
            slotProps={{
              textField: {
                fullWidth: true,
                error: !!errors[name],
                helperText: errors[name],
                sx: fieldSx,
                size: "medium",
              },
            }}
          />
        </Grid>
      );
    }
    
    if (type === "multiSelect") {
      // Ensure options are valid and properly formatted
      const validOptions = (options || []).filter(option => {
        if (!option) return false;
        if (typeof option === 'string' || typeof option === 'number') return true;
        if (typeof option === 'object') {
          return option.id || option.value || option.name;
        }
        return false;
      });

      return (
        <Grid item xs={12} md={4} key={uniqueKey}>
          <Autocomplete
            multiple
            options={validOptions}
            getOptionLabel={(option) => {
              // Ensure we always return a string
              if (typeof option === 'string') return option;
              if (typeof option === 'number') return String(option);
              if (option && typeof option === 'object') {
                const label = option.name || option.value || option.id || '';
                return String(label);
              }
              return '';
            }}
            value={validOptions.filter(option => {
              const optionId = option.id || option.value;
              const optionName = option.name || option.value;
              const formValue = formData[name] || [];
              
              return formValue.includes(optionId) || 
                     formValue.includes(optionName) ||
                     formValue.includes(String(optionId)) ||
                     formValue.includes(String(optionName));
            })}
            onChange={(event, newValue) => {
              const selectedValues = newValue.map(item => 
                item.id || item.value || item
              );
              handleChange({
                target: {
                  name,
                  value: selectedValues,
                },
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                required={required}
                error={!!errors[name]}
                helperText={errors[name]}
                sx={fieldSx}
                size="medium"
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {option.name || option.value || option}
                  </Typography>
                </Box>
              </Box>
            )}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.id || option.value || option}
                  label={option.name || option.value || option}
                  sx={{
                    borderRadius: "8px",
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    fontWeight: 500,
                  }}
                />
              ))
            }
            filterOptions={(options, { inputValue }) => {
              const filterValue = inputValue.toLowerCase();
              return options.filter(option => {
                const optionText = String(option.name || option.value || option).toLowerCase();
                return optionText.includes(filterValue);
              });
            }}
            sx={{
              "& .MuiAutocomplete-inputRoot": {
                borderRadius: "16px",
              },
            }}
          />
        </Grid>
      );
    }

    if (type === "select" || type === "autocomplete") {
      // Ensure options are valid and properly formatted
      const validOptions = (options || []).filter(option => {
        if (!option) return false;
        if (typeof option === 'string' || typeof option === 'number') return true;
        if (typeof option === 'object') {
          return option.id || option.value || option.name;
        }
        return false;
      });


      return (
        <Grid item xs={12} md={4} key={uniqueKey}>
          <Autocomplete
            options={validOptions}
            getOptionLabel={(option) => {
              // Ensure we always return a string
              if (typeof option === 'string') return option;
              if (typeof option === 'number') return String(option);
              if (option && typeof option === 'object') {
                const label = option.name || option.value || option.id || '';
                return String(label);
              }
              return '';
            }}
            value={validOptions.find(option => {
              // More flexible matching for different data structures
              const optionId = option.id || option.value;
              const optionName = option.name || option.value;
              const formValue = formData[name];
              
              return optionId === formValue || 
                     optionName === formValue || 
                     String(optionId) === String(formValue) ||
                     String(optionName) === String(formValue);
            }) || null}
            onChange={(event, newValue) => {
              handleChange({
                target: {
                  name,
                  value: newValue ? newValue.id || newValue.value || newValue : "",
                },
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                required={required}
                error={!!errors[name]}
                helperText={errors[name]}
                sx={fieldSx}
                size="medium"
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {option.name || option.value || option}
                  </Typography>
                </Box>
              </Box>
            )}
            filterOptions={(options, { inputValue }) => {
              const filterValue = inputValue.toLowerCase();
              return options.filter(option => {
                const optionText = String(option.name || option.value || option).toLowerCase();
                return optionText.includes(filterValue);
              });
            }}
            sx={{
              "& .MuiAutocomplete-inputRoot": {
                borderRadius: "16px",
              },
            }}
          />
        </Grid>
      );
    }

    return (
      <Grid item xs={12} md={4} key={uniqueKey}>
        <TextField
          fullWidth
          label={label}
          name={name}
          type={type}
          value={formData[name] || ""}
          onChange={handleChange}
          required={required}
          multiline={multiline}
          rows={rows}
          error={!!errors[name]}
          helperText={errors[name]}
          InputProps={{
            ...(type === "date" || type === "time"
              ? {
                  endAdornment: (
                    <Box sx={{ pr: 1, color: theme.palette.primary.main }}>
                      {type === "date" ? <CalendarIcon fontSize="small" /> : <AccessTimeIcon fontSize="small" />}
                    </Box>
                  ),
                }
              : {}),
            ...((name === "first_name" || name === "last_name") && {
              endAdornment: (
                <Box sx={{ pr: 1, color: theme.palette.text.secondary, fontSize: "0.75rem" }}>
                  {(formData[name] || "").length}/100
                </Box>
              ),
            }),
          }}
          sx={fieldSx}
        />
      </Grid>
    );
  };

  const renderSection = (title, icon, fields, index) => (
    <Card
      key={`section_${index}`}
      elevation={0}
      sx={{
        borderRadius: "20px",
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`,
        mb: 4,
        boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        backdropFilter: "blur(10px)",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
        },
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 48,
              height: 48,
              borderRadius: "16px",
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: "white",
              mr: 2,
              boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
            }}
          >
            {React.cloneElement(icon, { sx: { fontSize: 24 } })}
          </Box>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 700, 
              color: theme.palette.text.primary,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {title}
          </Typography>
        </Box>
        <Divider sx={{ mb: 3, opacity: 0.3 }} />
        <Grid container spacing={3}>
          {fields.map(renderField)}
        </Grid>
      </CardContent>
    </Card>
  );

  if (userLoading || dropdownLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={60} sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  if (!currentUser) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load user data. Please log in again.
        </Alert>
        <Button variant="contained" onClick={() => navigate("/login-page")}>
          Go to Login
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.05)} 0%, ${alpha(theme.palette.background.default, 0.8)} 50%, ${alpha(theme.palette.secondary.light, 0.05)} 100%)`,
        py: 5,
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.1) 0%, transparent 50%)",
          pointerEvents: "none",
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Typography
          variant="h3"
          sx={{ 
            fontWeight: 800, 
            color: theme.palette.primary.dark, 
            mb: 4,
            textAlign: "center",
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            textShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          My Profile
        </Typography>

        <Paper
          elevation={0}
          sx={{
            borderRadius: "24px",
            p: { xs: 3, md: 5 },
            boxShadow: "0 12px 48px rgba(0,0,0,0.12)",
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.background.default, 0.95)} 100%)`,
            mb: 4,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            backdropFilter: "blur(20px)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
              mb: 5,
            }}
          >
            <Box sx={{ position: "relative", mr: { xs: 0, md: 5 }, mb: { xs: 3, md: 0 } }}>
              <Avatar
                src={profileImage}
                sx={{
                  width: 140,
                  height: 140,
                  border: `4px solid ${theme.palette.primary.main}`,
                  fontSize: "3rem",
                  bgcolor: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                  boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
                }}
              >
                {!profileImage && `${formData.first_name?.charAt(0)}${formData.last_name?.charAt(0)}`}
              </Avatar>
              <IconButton
                component="label"
                sx={{
                  position: "absolute",
                  bottom: 8,
                  right: 8,
                  bgcolor: theme.palette.primary.main,
                  color: "white",
                  border: "3px solid white",
                  width: 44,
                  height: 44,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
                  "&:hover": { 
                    bgcolor: theme.palette.primary.dark,
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <CameraIcon fontSize="small" />
                <input type="file" hidden accept="image/*" onChange={handleImageChange} />
              </IconButton>
            </Box>
            <Box sx={{ textAlign: { xs: "center", md: "left" }, flex: 1 }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 800, 
                  mb: 1,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {formData.first_name} {formData.last_name}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, justifyContent: { xs: "center", md: "flex-start" }, mb: 2 }}>
                {formData.email && (
                  <Chip
                    icon={<ContactIcon fontSize="small" />}
                    label={formData.email}
                    size="medium"
                    sx={{ 
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      borderRadius: "12px",
                      "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.2) },
                    }}
                  />
                )}
                {formData.mobile && (
                  <Chip
                    icon={<ContactIcon fontSize="small" />}
                    label={formData.mobile}
                    size="medium"
                    sx={{ 
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      color: theme.palette.success.main,
                      fontWeight: 600,
                      borderRadius: "12px",
                      "&:hover": { bgcolor: alpha(theme.palette.success.main, 0.2) },
                    }}
                  />
                )}
                {formData.qualification && (
                  <Chip
                    icon={<SchoolIcon fontSize="small" />}
                    label={
                      dropdowns.qualifications.find((q) => q.id === formData.qualification)?.name ||
                      formData.qualification
                    }
                    size="medium"
                    sx={{ 
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      color: theme.palette.info.main,
                      fontWeight: 600,
                      borderRadius: "12px",
                      "&:hover": { bgcolor: alpha(theme.palette.info.main, 0.2) },
                    }}
                  />
                )}
                {formData.complexion && (
                  <Chip
                    icon={<InfoIcon fontSize="small" />}
                    label={
                      complexionOptions.find((c) => c.id === formData.complexion)?.name ||
                      formData.complexion
                    }
                    size="medium"
                    sx={{ 
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      color: theme.palette.warning.main,
                      fontWeight: 600,
                      borderRadius: "12px",
                      "&:hover": { bgcolor: alpha(theme.palette.warning.main, 0.2) },
                    }}
                  />
                )}
                {formData.marital_status && (
                  <Chip
                    icon={<WorkIcon fontSize="small" />}
                    label={
                      maritalStatusOptions.find((m) => m.id === formData.marital_status)?.name ||
                      formData.marital_status
                    }
                    size="medium"
                    sx={{ 
                      bgcolor: alpha(theme.palette.secondary.main, 0.1),
                      color: theme.palette.secondary.main,
                      fontWeight: 600,
                      borderRadius: "12px",
                      "&:hover": { bgcolor: alpha(theme.palette.secondary.main, 0.2) },
                    }}
                  />
                )}
                {formData.income && (
                  <Chip
                    icon={<MoneyIcon fontSize="small" />}
                    label={
                      incomeOptions.find((i) => i.id === formData.income)?.name ||
                      formData.income
                    }
                    size="medium"
                    sx={{ 
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      color: theme.palette.success.main,
                      fontWeight: 600,
                      borderRadius: "12px",
                      "&:hover": { bgcolor: alpha(theme.palette.success.main, 0.2) },
                    }}
                  />
                )}
              </Box>
              {profileImage && (
                <Button
                  startIcon={<DeleteIcon />}
                  variant="text"
                  color="error"
                  size="small"
                  onClick={() => {
                    setProfileImage(null);
                    setImageFile(null);
                  }}
                  sx={{ fontWeight: 500 }}
                >
                  Remove Photo
                </Button>
              )}
              {imageFile && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleImageUpload}
                  disabled={loading}
                  sx={{ ml: 2, fontWeight: 500 }}
                >
                  {loading ? "Uploading..." : "Upload Photo"}
                </Button>
              )}
            </Box>
          </Box>

          {success && (
            <Fade in timeout={500}>
              <Alert
                severity="success"
                icon={<CheckCircleIcon />}
                sx={{ mb: 3, borderRadius: "8px" }}
                onClose={() => setSuccess("")}
              >
                {success}
              </Alert>
            </Fade>
          )}
          {errors.submit && (
            <Fade in timeout={500}>
              <Alert
                severity="error"
                icon={<WarningIcon />}
                sx={{ mb: 3, borderRadius: "8px" }}
                onClose={() => setErrors({})}
              >
                {errors.submit}
              </Alert>
            </Fade>
          )}

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <form onSubmit={handleSubmit}>
              {formSections.map((section, index) => renderSection(section.title, section.icon, section.fields, index))}
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 5 }}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  disabled={loading}
                  sx={{ 
                    borderRadius: "16px", 
                    px: 6, 
                    py: 1.5,
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
                    "&:hover": {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      transform: "translateY(-2px)",
                      boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  {loading ? "Saving..." : "Save Changes"}
                </Button>
              </Box>
            </form>
          </LocalizationProvider>
        </Paper>
      </Container>
    </Box>
  );
};

export default Profile;